import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import type { Fixture } from '@h2h/shared';
import { BetSlipFooter, CardBuilder, type ScorerOptions } from '../../../../components/play/SlatePickFlow';
import { SCREEN_COLUMN } from '../../../../components/ui/AppShell';
import { cn } from '../../../../lib/cn';
import type { DraftCard } from '../../../../lib/picks';
import { getOverlayRoot, useOverlayChromeLock } from '../../../../lib/overlayRoot';
import { EASE_OUT_IOS, SHEET_DISMISS, SPRING_SHEET, prefersReducedMotion, useIsDesktop } from '../../../../lib/motion';

/**
 * Full-screen card builder, presented as a slide-up overlay over the League Hub.
 * It owns its own scroll and pins the bet-slip footer to the bottom, covering the
 * app's tab bar while you build (the same focused surface the standalone picks
 * route uses) — closing returns you straight to the inline build overview.
 *
 * Opening uses **prepare-then-animate**: the (heavy) builder tree is mounted and
 * painted at its off-screen start position first, then slid up on a later frame
 * (`raised`). Kicking the spring off on the same commit as the mount makes the big
 * first paint compete with the slide and drops frames — the jittery open. Painting
 * first keeps the slide itself pure compositor work, so it stays buttery.
 *
 * Closing slides down on a short, deterministic **tween** (not the present spring):
 * a near-critically-damped spring sliding a full-viewport surface has a long
 * asymptotic tail, so it decelerates and lingers at the bottom edge before framer
 * unmounts it — the "janky" close. A fixed-duration ease (SHEET_DISMISS) leaves crisply.
 */
export function CardBuilderSheet({
  open,
  fixture,
  card,
  legCatalog,
  statsEnabled,
  scorer,
  flag,
  frozen,
  pending,
  onChange,
  onClose
}: {
  open: boolean;
  fixture: Fixture | undefined;
  card: DraftCard | null;
  legCatalog: readonly string[];
  statsEnabled: boolean;
  scorer: ScorerOptions | null;
  flag: boolean;
  frozen: boolean;
  pending: boolean;
  onChange: (next: DraftCard) => void;
  onClose: () => void;
}) {
  const reduce = prefersReducedMotion();
  const wide = useIsDesktop();
  const [raised, setRaised] = useState(false);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    if (open) {
      setExiting(false);
    } else if (fixture && card) {
      setExiting(true);
    }
  }, [open, fixture, card]);

  useOverlayChromeLock(open || exiting);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Let the off-screen sheet paint once, then raise it on the next frame so the
  // builder's first paint never lands on the same frame as the slide (kills the jank).
  useEffect(() => {
    if (!open) {
      setRaised(false);
      return;
    }
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setRaised(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      cancelAnimationFrame(inner);
    };
  }, [open]);

  if (typeof document === 'undefined') return null;

  // Tablet + desktop: right-docked drawer (fixture list stays visible left). Phones: full-screen slide-up.
  return createPortal(
    <AnimatePresence initial={false} onExitComplete={() => setExiting(false)}>
      {open && fixture && card && wide && (
        <motion.div
          key="card-builder-backdrop"
          aria-hidden
          onClick={onClose}
          className="pointer-events-auto fixed inset-0 bg-night/40 backdrop-blur-[2px]"
          initial={{ opacity: 0 }}
          animate={{ opacity: raised ? 1 : 0 }}
          exit={{ opacity: 0, transition: { duration: 0.2, ease: EASE_OUT_IOS } }}
        />
      )}
      {open && fixture && card && (
        <motion.div
          key="card-builder-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Build your card"
          className={cn(
            'pointer-events-auto fixed flex flex-col bg-white',
            wide
              ? 'right-0 top-0 bottom-0 w-[min(380px,46vw)] border-l border-black/10 shadow-[-26px_0_70px_-34px_rgba(20,20,50,0.55)] lg:w-[min(460px,94vw)]'
              : 'inset-0 w-full pt-safe'
          )}
          initial={reduce ? { opacity: 0 } : wide ? { x: '100%' } : { y: '100%' }}
          animate={
            reduce
              ? { opacity: raised ? 1 : 0 }
              : wide
                ? { x: raised ? 0 : '100%' }
                : { y: raised ? 0 : '100%' }
          }
          exit={
            reduce
              ? { opacity: 0 }
              : wide
                ? { x: '100%', transition: SHEET_DISMISS }
                : { y: '100%', transition: SHEET_DISMISS }
          }
          transition={reduce ? { duration: 0.18, ease: EASE_OUT_IOS } : SPRING_SHEET}
          style={{ willChange: 'transform' }}
        >
          <div className={cn('flex min-h-0 flex-1 flex-col', wide ? 'px-4' : SCREEN_COLUMN)}>
            <div className="native-scroll no-scrollbar min-h-0 flex-1 overflow-y-auto">
              <CardBuilder
                fixture={fixture}
                card={card}
                legCatalog={legCatalog}
                statsEnabled={statsEnabled}
                scorer={scorer}
                flag={flag}
                frozen={frozen}
                onBack={onClose}
                onChange={onChange}
              />
            </div>
            <BetSlipFooter
              card={card}
              frozen={frozen}
              pending={pending}
              onLock={onClose}
              lockLabel={frozen ? 'Back to round' : undefined}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    getOverlayRoot()
  );
}
